/**
 * Session Service
 *
 * Handles session management operations
 */

import { BaseService } from "../base/BaseService";
import {
  SessionData,
  CreateSessionResponse,
  UserSession,
  DeleteSessionResponse,
  LogoutAllDevicesResponse,
  UpdateSessionHeartbeatResponse,
} from "./types";

const AUTH_SERVICE_URL = "auth";

/**
 * Session Service
 * Handles all session-related operations
 */
export class SessionService extends BaseService {
  constructor() {
    super(AUTH_SERVICE_URL);
  }

  /**
   * Create a session record
   * POST /sessions
   */
  createSession(sessionData: SessionData): Promise<CreateSessionResponse> {
    return this.post("/sessions", sessionData);
  }

  /**
   * Get user sessions
   * GET /sessions/:userId
   */
  async getUserSessions(userId: string): Promise<UserSession[]> {
    const response = await this.get<{ success: boolean; data: UserSession[] }>(
      `/sessions/${userId}`
    );
    return response.data ?? [];
  }

  /**
   * Delete a session (logout from device)
   * DELETE /sessions/:sessionId
   */
  deleteSession(sessionId: string): Promise<DeleteSessionResponse> {
    return this.delete(`/sessions/${sessionId}`);
  }

  /**
   * Logout from all devices
   * DELETE /sessions/all/:userId
   */
  logoutAllDevices(userId: string): Promise<LogoutAllDevicesResponse> {
    return this.delete(`/sessions/all/${userId}`);
  }

  /**
   * Update session heartbeat
   * PUT /sessions/:sessionId/heartbeat
   */
  updateSessionHeartbeat(sessionId: string): Promise<UpdateSessionHeartbeatResponse> {
    return this.put(`/sessions/${sessionId}/heartbeat`, {});
  }

  /**
   * Get active session count
   * GET /sessions/:userId/active-count
   */
  getActiveSessionCount(userId: string): Promise<{ count: number }> {
    return this.get(`/sessions/${userId}/active-count`);
  }
}
